import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import useTheme from '@mui/material/styles/useTheme';
import { useSelector } from 'react-redux';
import { getUserPermissions, updateUserData, userSelector } from 'redux/reducers/user';
import { pushMessage } from 'redux/reducers/messages';
import { useAppDispatch } from 'redux/store';
import {
    mapOptionIdToPrimarySportValue,
    mapPrimarySportValueToOptionId,
    PrimarySportOptionId,
    PRIMARY_SPORT_OPTIONS,
} from 'utils/primarySport';
import { useSpeedUnitPreference, PreferredSpeedUnit } from 'hooks/useSpeedUnitPreference';

const PrimarySportSelectorView = () => {
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const { userData } = useSelector(userSelector);
    const [selectedOptionId, setSelectedOptionId] = useState<PrimarySportOptionId>('track');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { unit, setUnit } = useSpeedUnitPreference();

    useEffect(() => {
        setSelectedOptionId(mapPrimarySportValueToOptionId(userData?.primary_sport));
    }, [userData?.primary_sport]);

    const selectedStoredValue = useMemo(
        () => mapOptionIdToPrimarySportValue(selectedOptionId),
        [selectedOptionId],
    );

    const showSpeedToggle = selectedOptionId === 'baseball' || selectedOptionId === 'softball';

    const hasChanges = (userData?.primary_sport || null) !== selectedStoredValue;

    const handleSelectChange = (event: SelectChangeEvent<PrimarySportOptionId>) => {
        setSelectedOptionId(event.target.value as PrimarySportOptionId);
    };

    const handleSpeedUnitChange = (
        _: React.MouseEvent<HTMLElement>,
        newUnit: PreferredSpeedUnit | null,
    ) => {
        if (newUnit) setUnit(newUnit);
    };

    const handleSave = async () => {
        if (!userData?.uuid) return;
        setIsSubmitting(true);

        const isBaseball = selectedOptionId === 'baseball' || selectedOptionId === 'softball';
        const needsDefaultSpeedUnit = isBaseball && !userData?.preferred_speed_unit;

        const result = await dispatch(
            updateUserData({
                userUuid: userData.uuid,
                first_name: null,
                last_name: null,
                email: null,
                user_name: null,
                phone: null,
                profile_photo: null,
                primary_sport: selectedStoredValue,
                preferred_speed_unit: needsDefaultSpeedUnit ? 'fps' : undefined,
            }),
        );

        setIsSubmitting(false);

        if (updateUserData.fulfilled.match(result)) {
            dispatch(getUserPermissions({ id: userData.uuid }));
            dispatch(
                pushMessage({
                    status: 'success',
                    message: 'Primary sport updated successfully.',
                }),
            );
            return;
        }

        dispatch(
            pushMessage({
                status: 'error',
                message: 'Something went wrong, please try again',
            }),
        );
    };

    return (
        <>
            <FormControl fullWidth variant="filled" sx={{ marginBottom: theme.spacing(10) }}>
                <InputLabel id="primary-sport-selector-label">Primary Sport</InputLabel>
                <Select
                    labelId="primary-sport-selector-label"
                    value={selectedOptionId}
                    onChange={handleSelectChange}
                    disabled={!userData?.uuid || isSubmitting}
                >
                    {PRIMARY_SPORT_OPTIONS.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {showSpeedToggle && (
                <Box sx={{ marginBottom: theme.spacing(10) }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Speed Display Unit
                    </Typography>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 1 }}
                    >
                        MLB teams commonly use feet per second (ft/s)
                    </Typography>
                    <ToggleButtonGroup
                        value={unit}
                        exclusive
                        onChange={handleSpeedUnitChange}
                        fullWidth
                        size="small"
                    >
                        <ToggleButton value="fps">ft/s (FPS)</ToggleButton>
                        <ToggleButton value="mph">mph (MPH)</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            )}

            <Button
                size="large"
                type="button"
                color="primary"
                variant="contained"
                onClick={handleSave}
                disabled={!hasChanges || isSubmitting || !userData?.uuid}
                sx={{ width: '100%', marginBottom: theme.spacing(10) }}
            >
                {isSubmitting ? (
                    <>
                        Updating Primary Sport
                        <CircularProgress
                            disableShrink
                            size={30}
                            color="inherit"
                            sx={{ marginLeft: theme.spacing(4) }}
                        />
                    </>
                ) : (
                    'Update Primary Sport'
                )}
            </Button>
        </>
    );
};

export default PrimarySportSelectorView;
